import { ServiceAttributeIcon } from '@/components/icons/ServiceAttributeIcon';
import { TransportationTypeIcon } from '@/components/icons/TransportationTypeIcon';
import CarrierLogo from '@/components/purchase/journey/CarrierLogo';
import LegIntermediateStop from '@/components/purchase/journey/LegIntermediateStop';
import LegStop from '@/components/purchase/journey/LegStop';
import { TransText } from '@/i18n/trans/text';
import type { LegDTO } from '@/types/dto/agent';
import { cn } from '@/utils/cn';
import {
  formatDay,
  formatDuration,
  stripTimeZoneOffset,
} from '@/utils/dateTime';
import {
  Accordion,
  Divider,
  Icons,
  Typography,
} from '@packages/webshop-design-system';
import type { FC } from 'react';

interface StopDetailsProps {
  leg: LegDTO;
  reverse?: boolean;
}

const StopDetails: FC<StopDetailsProps> = ({ leg, reverse }) => {
  const departureDay =
    leg.departureTime && formatDay(stripTimeZoneOffset(leg.departureTime));
  const arrivalDay =
    leg.arrivalTime && formatDay(stripTimeZoneOffset(leg.arrivalTime));
  const duration = formatDuration(leg.duration)?.replace(/(\d+)m\b/g, '$1 min');
  const ptModeCode = leg.ptMode?.code?.toUpperCase();

  return (
    <div className={cn('flex flex-col gap-4', { 'flex-col-reverse': reverse })}>
      <div className="flex gap-3">
        <div className="relative flex flex-col items-center px-1 pb-1.5 pt-1.5">
          <TransportationTypeIcon
            ptMode={leg.ptMode}
            className={cn('relative z-10', {
              'text-primary': ptModeCode !== 'WALK',
              'text-secondary': ptModeCode === 'WALK',
            })}
          />
          <div
            className={cn(
              'absolute bottom-4 left-1/2 top-6 w-0.5 -translate-x-1/2',
              ptModeCode === 'WALK'
                ? 'border-l-2 border-dashed border-neutral-light'
                : 'bg-neutral-light'
            )}
          />
          <Icons.destination className="relative z-10 mt-auto text-primary" />
        </div>
        <div className="flex w-full flex-col gap-2 py-0.5">
          {leg.originStop && (
            <LegStop
              stop={leg.originStop}
              ptMode={leg.ptMode}
              time={leg.departureTime}
              day={departureDay}
            />
          )}
          {ptModeCode === 'WALK' && duration && (
            <Typography variant="body2" className="text-neutral">
              {duration}
            </Typography>
          )}
          {!!leg.intermediateStops?.length && (
            <Accordion
              className="border-none"
              classNames={{
                trigger: 'justify-start gap-1 p-0 hover:no-underline',
                content: 'flex flex-col gap-2',
              }}
              trigger={
                <Typography variant="body2-bold" className="text-primary">
                  <TransText
                    i18nKey="stopsCount"
                    values={{ count: leg.intermediateStops.length }}
                  />
                </Typography>
              }
            >
              <Divider className="mt-1" />
              <table className="w-full table-fixed">
                <thead>
                  <tr>
                    <th className="pr-2 text-left"></th>
                    <th className="w-16 px-2 text-center sm:w-20">
                      <Typography variant="body2-bold">
                        <TransText i18nKey="arrival" />
                      </Typography>
                    </th>
                    <th className="w-16 px-2 text-center sm:w-20">
                      <Typography variant="body2-bold">
                        <TransText i18nKey="departure" />
                      </Typography>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {leg.intermediateStops.map((stop, key) => (
                    <LegIntermediateStop key={key} stop={stop} />
                  ))}
                </tbody>
              </table>
              <Divider />
            </Accordion>
          )}
          {leg.destinationStop && (
            <LegStop
              stop={leg.destinationStop}
              ptMode={leg.ptMode}
              time={leg.arrivalTime}
              day={arrivalDay}
            />
          )}
        </div>
      </div>

      <CarrierLogo carrier={leg.carrier} />

      {ptModeCode !== 'WALK' && (
        <div className="flex">
          <div className="flex items-center gap-1 rounded-l bg-primary-light px-2">
            <TransportationTypeIcon ptMode={leg.ptMode} className="text-dark" />
            <Typography variant="body2-bold">{leg.serviceCode}</Typography>
          </div>
          <div className="flex w-fit gap-2 rounded-r bg-primary-pale p-1.5">
            {leg.serviceAttributes?.map((attributes, key) => (
              <ServiceAttributeIcon key={key} {...attributes} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StopDetails;
